import React, { useState, useRef, useEffect } from "react";
import CountUp from "react-countup";
import PhoneInput from "react-phone-input-2";
import logo from "../../../assets/form/Logo.png";
import { motion } from "framer-motion";

const Form = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [projectScope, setProjectScope] = useState("");
  const [hasScrolled, setHasScrolled] = useState(false); // For tracking if user has scrolled
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setHasScrolled(true); // Trigger count up when the section becomes visible
        } else {
          setHasScrolled(false); // Reset so count up triggers again when visible
        }
      },
      {
        root: null,
        threshold: 0.2, // Trigger when 20% of the section is in view
      }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const homeForm = { name, email, phone, projectScope };
    console.log(homeForm);
  };

  return (
    <>
      <motion.section
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.8, type: "spring" }}
        viewport={{ once: true }}
        className="flex justify-center items-center py-16 w-full max-md:px-5 max-md:mt-10 max-md:max-w-full"
        id="need-an-expert-form"
      >
        <div className="flex flex-col w-full max-w-[1120px] max-md:max-w-full">
          <div className="flex w-full max-md:flex-col">
            <aside className="flex flex-col w-7/12 max-w-full max-md:w-full">
              <header className="text-3xl sm:text-5xl font-medium leading-10 light-title max-md:max-w-full">
                Expertise at Scale
              </header>
              <p className="mt-3.5 text-lg text-color max-md:max-w-full">
                Connecting you to top-tier professionals across diverse industries. We facilitate strategic insights and foster enduring collaborations.
              </p>

              <div className="form-left-section">
                <div ref={counterRef} className="flex justify-around mt-24 max-md:flex-col max-md:mt-10">
                  <section className="flex flex-col font-medium text-center max-md:my-5">
                    <h2 className="text-6xl leading-10 light-title max-md:text-4xl">
                      {hasScrolled && <CountUp start={0} end={8000} duration={5.75} suffix="+" />}
                    </h2>
                    <p className="mt-4 text-lg text-color leading-[48.06px]">Engagements Completed</p>
                  </section>
                  <section className="flex flex-col font-medium text-center max-md:my-5">
                    <h2 className="text-6xl leading-10 light-title max-md:text-4xl">
                      {hasScrolled && <CountUp start={0} end={400} duration={5} suffix="K+" />}
                    </h2>
                    <p className="self-center mt-4 text-lg text-color leading-[48.06px]">Expert Panel</p>
                  </section>
                </div>

                <div className="flex justify-around mt-28 max-md:flex-col max-md:mt-2">
                  <section className="flex flex-col font-medium text-center max-md:my-5">
                    <h2 className="self-center text-6xl leading-10 light-title max-md:text-4xl">
                      {hasScrolled && <CountUp start={0} end={95} duration={5} suffix="%" />}
                    </h2>
                    <p className="mt-4 text-lg text-color leading-[48.06px]">Returning clients</p>
                  </section>
                  <section className="flex flex-col font-medium text-center max-md:my-5">
                    <h2 className="text-6xl leading-10 light-title max-md:text-4xl">
                      {hasScrolled && <CountUp start={0} end={120} duration={5} suffix="+" />}
                    </h2>
                    <p className="mt-4 text-lg text-color leading-[48.06px]">Industry Niches</p>
                  </section>
                </div>
              </div>
            </aside>

            <form
              className="flex flex-col w-5/12 self-end ml-20 max-w-full max-md:w-full max-md:mt-10 max-md:ml-2.5 form-right-section"
              onSubmit={(e) => handleSubmit(e)}
            >
              <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.2, type: "spring" }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-xl pb-8"
              >
                <div className="flex gap-2.5 px-4 py-2 text-xl font-semibold text-white whitespace-nowrap bg-light-indigo border-dark-indigo border-b-[3px] rounded-t-xl shadow-sm max-md:flex-wrap">
                  <img alt="Expertefy Logo" src={logo} className="shrink-0 aspect-[1.03] w-[33px]" />
                  <span className="flex-auto my-auto">EXPERTEFY</span>
                </div>
                <div className="flex flex-col px-5 max-md:max-w-full">
                  <h2 className="text-xl font-bold text-color leading-[48.09px]">Got a Project in Mind?</h2>
                  <label htmlFor="name" className="sr-only">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    className="box-border flex relative flex-col shrink-0 p-2.5  border-solid"
                    placeholder="Your Name"
                    aria-label="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />

                  <label htmlFor="email" className="sr-only">Your Email</label>
                  <input
                    type="email"
                    id="email"
                    className="box-border flex relative flex-col shrink-0 p-2.5 mt-5  border-solid"
                    placeholder="Your Email"
                    aria-label="Your Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />

                  <PhoneInput
                    className="box-border flex relative flex-col shrink-0 p-1.5 mt-5  border-b-2 border-solid"
                    inputStyle={{ borderStyle: "none", width: "inherit" }}
                    dropdownStyle={{
                      borderStyle: "solid",
                      border: 1,
                      borderColor: "#6369C4",
                      backgroundColor: "#fff",
                    }}
                    buttonStyle={{ borderStyle: "none", border: 0, backgroundColor: "#fff" }}
                    required
                    country={"us"}
                    value={phone}
                    onChange={(ph) => setPhone(ph)}
                  />

                  <label htmlFor="projectScope" className="sr-only">Let us know the project scope</label>
                  <textarea
                    id="projectScope"
                    className="box-border flex relative flex-col shrink-0 px-2.5 pt-2.5 pb-4 my-5  border-solid"
                    placeholder="Let us know the project scope"
                    aria-label="Project Scope"
                    value={projectScope}
                    onChange={(e) => setProjectScope(e.target.value)}
                    required
                  ></textarea>
                  <button className="justify-center dark-button items-center self-center px-16 py-5 mt-7 max-w-full text-2xl text-center text-white rounded-lg  shadow-xl w-[323px] max-md:px-5">
                    Submit Request
                  </button>
                </div>
              </motion.div>
            </form>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default Form;
